/**
 * Network Change Handler (Web)
 *
 * Ported from MobileCh3/src/lib/sip/networkChangeHandler.ts
 *
 * Listens for network transitions during active calls and recovers audio by:
 *   1. Reconnecting the SIP WebSocket transport on the new interface
 *   2. Triggering a WebRTC ICE restart so new candidates are gathered
 *
 * Web-specific: Uses navigator.connection API and online/offline events
 * instead of React Native's NetInfo.
 */

import logger from '../services/logger';
import { getAudioFlags } from '../services/abTest/variant';

/**
 * Maximum wait for the SIP transport to reconnect before triggering ICE
 * restart anyway. When the JsSIP UA fires 'connected' first we use that
 * signal and skip the remaining wait; this is only the fallback ceiling.
 */
const ICE_RESTART_DELAY_MS = 4000;

/**
 * Debounce window for rapid network flaps.
 * Only the last softReconnect() call in this window actually executes,
 * preventing overlapping WebSocket disconnect/connect cycles.
 */
const RECONNECT_DEBOUNCE_MS = 300;

/**
 * Delay between transport reconnect and sending the ICE-restart re-INVITE.
 * Gives the SIP server time to clean up stale INVITE transactions that were
 * in-flight when the WebSocket dropped, avoiding a glare condition.
 */
const REINVITE_DELAY_AFTER_RECONNECT_MS = 1000;

/**
 * How long to wait for connectivity to return after a complete network loss
 * before force-disconnecting the WebSocket.
 */
const NETWORK_LOSS_RECONNECT_WAIT_MS = 10000;

/**
 * Maximum time the WebSocket transport is allowed to stay down during an
 * active call before the session is terminated locally.
 *
 * 30s is long enough to survive brief interruptions (elevator, tunnel)
 * but short enough to prevent calls from hanging for minutes.
 */
const CALL_WATCHDOG_TIMEOUT_MS = 30000;

/**
 * Sustained-loss grace window for the WS decoupling model (variant C,
 * gated by `flags.wsSustainedLossGrace`).
 *
 * SIP WebSocket is an independent TCP/TLS connection from the UDP/TURN
 * media path. A brief signaling flap on a home AP (UniFi U6 beacon
 * collisions, WiFi power-save wake, transient TLS packet loss) does not
 * imply media disruption — mobile users observed the old every-blip ICE
 * restart producing audible 1–3 s audio gaps even though media was
 * flowing fine. Meet/Slack don't exhibit this because they decouple the
 * two paths in exactly this way.
 *
 * When this grace window is active, `ws_disconnected_mid_call` only
 * softReconnects the transport; ICE restart only fires if the WS stays
 * down past WS_SUSTAINED_LOSS_ICE_RESTART_MS. The permanent UA
 * `connected` listener below cancels the timer on fast recovery.
 *
 * Real network-type changes (WiFi ↔ LTE) are still handled immediately
 * by `onConnectionChange` — they DO need an ICE restart and do not
 * wait for this grace window.
 */
const WS_SUSTAINED_LOSS_ICE_RESTART_MS = 8000;

/**
 * Bounded window during which we watch for the PeerConnection's
 * iceConnectionState to flip to `disconnected` / `failed` after a fast WS
 * recovery. Consent-liveness timers in Chromium/Firefox can take up to
 * ~15 s to expire, so a one-shot probe at T+3 s would report the path as
 * healthy on a truly broken candidate pair. We register an event listener
 * instead and only unregister it once the window expires (→ path
 * confirmed healthy) or state actually transitions to a terminal value
 * (→ fire ICE restart).
 *
 * Uses iceConnectionState rather than RTP packet counts because the
 * variant-C Opus DTX flag on the wire produces ZERO inbound packets
 * during legitimate silence / mute / hold — an RTP-delta probe would
 * fire false ICE restarts on every silent turn-holder.
 */
const ICE_STATE_PROBE_WINDOW_MS = 20000;

/**
 * Extract a summary of the current network state for logging.
 */
function networkSummary(): Record<string, unknown> {
    const conn = (navigator as any).connection;
    return {
        online: navigator.onLine,
        effectiveType: conn?.effectiveType ?? null,
        type: conn?.type ?? null,
        downlink: conn?.downlink ?? null,
        rtt: conn?.rtt ?? null,
    };
}

/**
 * Register a network-change listener for an active call.
 * Must be called when the call is confirmed (session established).
 * Returns a cleanup function -- call it when the call ends.
 *
 * @param session       - JsSIP RTCSession (must be established)
 * @param softReconnect - Reconnects SIP WebSocket transport
 * @param ua            - JsSIP UA instance; when provided the ICE restart
 *                        fires on the UA 'connected' event instead of a
 *                        blind timer, making recovery event-driven.
 * @param callId        - Call identifier for log correlation
 */
export function setupNetworkChangeHandling(
    session: any,
    softReconnect: () => void,
    ua: any,
    callId?: string,
): () => void {
    let lastEffectiveType: string | null = (navigator as any).connection?.effectiveType ?? null;
    let iceRestartTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pendingConnectedListener: (() => void) | null = null;
    let pendingDisconnectedListener: (() => void) | null = null;
    // Permanent UA 'connected' listener (attached once). Distinct from
    // `pendingConnectedListener` which is the one-shot used by scheduleIceRestart.
    // This one resets reconnecting / watchdog / sustained-loss timer on every
    // WS recovery without triggering an ICE restart.
    let pendingUaConnectedListener: (() => void) | null = null;
    let cancelled = false;
    let reconnecting = false;
    let callWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
    // Sustained-loss safety net armed on WS disconnect, cancelled on WS recovery.
    // Fires scheduleIceRestart only if WS stays down past the grace window.
    let sustainedLossTimer: ReturnType<typeof setTimeout> | null = null;
    let wsDisconnectedAt: number | null = null;
    // Event-driven ICE-state probe armed after a fast WS recovery inside the
    // grace window. Watches pc.iceConnectionState for a bounded window;
    // fires ICE restart if the state transitions to `disconnected`/`failed`.
    // Covers the "WS flapped AND media is also dead" case that
    // navigator.connection may miss on some browsers (Firefox/Safari have
    // no Network Information API, Chromium sometimes misses quiet roams).
    let iceStateProbeTimer: ReturnType<typeof setTimeout> | null = null;
    let iceStateProbeCleanup: (() => void) | null = null;

    /**
     * Execute the ICE restart re-INVITE.
     */
    const triggerIceRestart = (reason: string) => {
        if (cancelled) return;

        // Guard: don't send re-INVITE if the WebSocket transport is still down.
        if (ua && typeof ua.isConnected === 'function' && !ua.isConnected()) {
            logger.warn('ICE restart deferred -- transport not yet connected, waiting for connected event', {
                source: 'networkHandler',
                event: 'ice_restart_deferred_transport_down',
                reason,
                callId,
            });

            if (pendingConnectedListener != null && ua) {
                ua.removeListener('connected', pendingConnectedListener);
                pendingConnectedListener = null;
            }
            const deferredRestartListener = () => {
                pendingConnectedListener = null;
                reconnecting = false;
                if (callWatchdogTimer != null) {
                    clearTimeout(callWatchdogTimer);
                    callWatchdogTimer = null;
                    logger.info('Call watchdog cancelled -- transport reconnected (deferred ICE restart path)', {
                        source: 'networkHandler',
                        event: 'call_watchdog_cancelled',
                        callId,
                    });
                }
                if (!cancelled) {
                    triggerIceRestart(reason);
                }
            };
            pendingConnectedListener = deferredRestartListener;
            ua.once('connected', deferredRestartListener);
            return;
        }

        let clearRestartFlag: () => void = () => {};
        let clearWatchdog: () => void = () => {};

        try {
            if (session.isEstablished()) {
                if (session.__iceRestartInProgress) {
                    logger.info('ICE restart skipped -- another restart re-INVITE already in progress', {
                        source: 'networkHandler',
                        event: 'ice_restart_skipped_in_progress',
                        reason,
                        callId,
                    });
                    return;
                }

                session.__iceRestartInProgress = true;

                clearRestartFlag = () => { session.__iceRestartInProgress = false; };
                const restartWatchdog = setTimeout(clearRestartFlag, 15000);
                clearWatchdog = () => clearTimeout(restartWatchdog);
                session.once('ended', () => { clearWatchdog(); clearRestartFlag(); });
                session.once('failed', () => { clearWatchdog(); clearRestartFlag(); });

                logger.info(`ICE restart triggered: ${reason}`, {
                    source: 'networkHandler',
                    event: 'ice_restart_triggered',
                    reason,
                    callId,
                    ...networkSummary(),
                });

                session.renegotiate(
                    { rtcOfferConstraints: { iceRestart: true } },
                    () => {
                        // done callback -- re-INVITE answered with 200 OK
                        clearWatchdog();
                        clearRestartFlag();
                    },
                );
            } else {
                logger.debug('ICE restart skipped -- session no longer established', {
                    source: 'networkHandler',
                    event: 'ice_restart_skipped',
                    reason,
                    callId,
                });
            }
        } catch (err) {
            clearWatchdog();
            clearRestartFlag();
            logger.warn('ICE restart failed after network change', {
                source: 'networkHandler',
                event: 'ice_restart_error',
                reason,
                callId,
                errorMessage: (err as Error)?.message,
            });
        }
    };

    /**
     * Schedule an ICE restart for a specific network transition.
     *
     * Event-driven: listen for the UA 'connected' event and trigger on that signal.
     * Fixed timeout acts as a ceiling in case 'connected' never fires.
     */
    const scheduleIceRestart = (reason: string) => {
        if (iceRestartTimer != null) {
            clearTimeout(iceRestartTimer);
            iceRestartTimer = null;
        }
        if (pendingConnectedListener != null && ua) {
            ua.removeListener('connected', pendingConnectedListener);
            pendingConnectedListener = null;
        }

        if (ua) {
            let triggered = false;
            const onConnected = () => {
                if (triggered || cancelled) return;
                triggered = true;
                pendingConnectedListener = null;
                reconnecting = false;

                if (callWatchdogTimer != null) {
                    clearTimeout(callWatchdogTimer);
                    callWatchdogTimer = null;
                    logger.info('Call watchdog cancelled -- transport reconnected in time', {
                        source: 'networkHandler',
                        event: 'call_watchdog_cancelled',
                        callId,
                    });
                }
                if (iceRestartTimer != null) {
                    clearTimeout(iceRestartTimer);
                    iceRestartTimer = null;
                }
                logger.info(`Transport reconnected -- triggering ICE restart in ${REINVITE_DELAY_AFTER_RECONNECT_MS}ms`, {
                    source: 'networkHandler',
                    event: 'ice_restart_transport_ready',
                    reason,
                    callId,
                });
                iceRestartTimer = setTimeout(() => {
                    iceRestartTimer = null;
                    triggerIceRestart(reason);
                }, REINVITE_DELAY_AFTER_RECONNECT_MS);
            };
            pendingConnectedListener = onConnected;
            ua.once('connected', onConnected);

            // Fallback ceiling
            iceRestartTimer = setTimeout(() => {
                if (triggered || cancelled) return;
                if (ua && typeof ua.isConnected === 'function' && !ua.isConnected()) {
                    logger.info('ICE restart fallback: WS still down, deferring to connected event', {
                        source: 'networkHandler',
                        event: 'ice_restart_fallback_ws_still_down',
                        callId,
                    });
                    return;
                }
                triggered = true;
                pendingConnectedListener = null;
                ua.removeListener('connected', onConnected);
                logger.warn(`Transport did not signal 'connected' within ${ICE_RESTART_DELAY_MS}ms -- triggering ICE restart anyway`, {
                    source: 'networkHandler',
                    event: 'ice_restart_timeout_fallback',
                    reason,
                    callId,
                });
                triggerIceRestart(reason);
            }, ICE_RESTART_DELAY_MS);
        } else {
            // No UA -- fall back to blind fixed delay
            iceRestartTimer = setTimeout(() => {
                iceRestartTimer = null;
                triggerIceRestart(reason);
            }, ICE_RESTART_DELAY_MS);
        }
    };

    // ---- Browser online/offline events ----
    const onOffline = () => {
        if (cancelled) return;
        logger.warn('Network lost during call (offline event)', {
            source: 'networkHandler',
            event: 'network_lost',
            callId,
            ...networkSummary(),
        });

        if (!reconnecting) {
            if (reconnectTimer != null) clearTimeout(reconnectTimer);
            reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                if (cancelled) return;
                logger.warn(`Network did not recover within ${NETWORK_LOSS_RECONNECT_WAIT_MS}ms -- forcing WS disconnect`, {
                    source: 'networkHandler',
                    event: 'network_loss_reconnect_timeout',
                    callId,
                    ...networkSummary(),
                });
                softReconnect();
            }, NETWORK_LOSS_RECONNECT_WAIT_MS);
        }
    };

    const onOnline = () => {
        if (cancelled) return;
        logger.info('Network recovered during call (online event)', {
            source: 'networkHandler',
            event: 'network_online',
            callId,
            ...networkSummary(),
        });

        // Cancel the network loss timeout since we're back online
        if (reconnectTimer != null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }

        // Debounce transport reconnect
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            if (cancelled) return;
            softReconnect();
        }, RECONNECT_DEBOUNCE_MS);

        scheduleIceRestart('network_online');
    };

    // ---- navigator.connection change event (network type transitions) ----
    const onConnectionChange = () => {
        if (cancelled) return;
        const conn = (navigator as any).connection;
        const newEffectiveType = conn?.effectiveType ?? null;

        if (newEffectiveType === lastEffectiveType) return;

        const prevType = lastEffectiveType;
        lastEffectiveType = newEffectiveType;

        logger.info(`Network type changed during call: ${prevType} -> ${newEffectiveType}`, {
            source: 'networkHandler',
            event: 'network_type_changed',
            prevType,
            newType: newEffectiveType,
            callId,
            ...networkSummary(),
        });

        // Cancel any pending network loss timeout
        if (reconnectTimer != null) {
            clearTimeout(reconnectTimer);
        }
        reconnecting = true;
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            if (cancelled) return;
            softReconnect();
        }, RECONNECT_DEBOUNCE_MS);

        scheduleIceRestart(`network_type_changed: ${prevType} -> ${newEffectiveType}`);
    };

    // Register browser event listeners
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    const connection = (navigator as any).connection;
    if (connection) {
        connection.addEventListener('change', onConnectionChange);
    }

    // ---- UA-level WebSocket disconnection handling ----
    //
    // IMPORTANT (variant C / wsSustainedLossGrace): WS drop alone does NOT
    // trigger an ICE restart. SIP WebSocket is an independent TCP/TLS
    // connection from the UDP/TURN media path — brief signaling flaps do not
    // imply media disruption. We softReconnect the transport, arm a
    // sustained-loss safety-net timer, and only fall back to ICE restart if
    // the WS stays down long enough to suggest the media path may also be
    // affected. The permanent `onUAConnected` listener below cancels the
    // timer on fast recovery.
    //
    // When `flags.wsSustainedLossGrace` is off (variants A, B), the original
    // immediate `scheduleIceRestart('ws_disconnected_mid_call')` path runs —
    // byte-for-byte behaviour preserved.
    if (ua) {
        const onUADisconnected = () => {
            if (cancelled) return;
            if (reconnecting) return;
            reconnecting = true;
            const useGrace = getAudioFlags().wsSustainedLossGrace;
            wsDisconnectedAt = Date.now();

            logger.warn(
                useGrace
                    ? 'UA WebSocket disconnected during call -- reconnecting transport only (ICE restart only if sustained)'
                    : 'UA WebSocket disconnected during call -- triggering transport reconnect + ICE restart',
                {
                    source: 'networkHandler',
                    event: 'ws_disconnected_mid_call',
                    callId,
                    sustainedLossGrace: useGrace,
                    ...networkSummary(),
                },
            );

            // Start call watchdog
            if (callWatchdogTimer == null) {
                logger.info(`Call watchdog started -- will terminate session if WS does not recover within ${CALL_WATCHDOG_TIMEOUT_MS}ms`, {
                    source: 'networkHandler',
                    event: 'call_watchdog_started',
                    timeoutMs: CALL_WATCHDOG_TIMEOUT_MS,
                    callId,
                });
                callWatchdogTimer = setTimeout(() => {
                    callWatchdogTimer = null;
                    if (cancelled) return;
                    logger.warn(`Call watchdog fired -- WebSocket down for ${CALL_WATCHDOG_TIMEOUT_MS}ms, terminating session locally`, {
                        source: 'networkHandler',
                        event: 'call_watchdog_fired',
                        timeoutMs: CALL_WATCHDOG_TIMEOUT_MS,
                        callId,
                        ...networkSummary(),
                    });
                    try {
                        if (session.isEstablished()) {
                            session.terminate();
                        }
                    } catch (err) {
                        logger.warn('Call watchdog: session.terminate() threw', {
                            source: 'networkHandler',
                            event: 'call_watchdog_terminate_error',
                            callId,
                            errorMessage: (err as Error)?.message,
                        });
                    }
                }, CALL_WATCHDOG_TIMEOUT_MS);
            }

            // Debounce transport reconnect
            if (reconnectTimer != null) {
                clearTimeout(reconnectTimer);
            }
            reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                if (cancelled) return;
                softReconnect();
            }, RECONNECT_DEBOUNCE_MS);

            if (useGrace) {
                // Arm sustained-loss safety net. If WS recovers before it fires,
                // onUAConnected cancels it — no ICE restart in that case.
                if (sustainedLossTimer != null) {
                    clearTimeout(sustainedLossTimer);
                }
                sustainedLossTimer = setTimeout(() => {
                    sustainedLossTimer = null;
                    if (cancelled || !reconnecting) return;
                    logger.warn(`WS still down after ${WS_SUSTAINED_LOSS_ICE_RESTART_MS}ms -- falling back to ICE restart as recovery`, {
                        source: 'networkHandler',
                        event: 'ws_sustained_loss_ice_restart',
                        waitMs: WS_SUSTAINED_LOSS_ICE_RESTART_MS,
                        callId,
                    });
                    scheduleIceRestart('ws_disconnect_sustained');
                }, WS_SUSTAINED_LOSS_ICE_RESTART_MS);
            } else {
                scheduleIceRestart('ws_disconnected_mid_call');
            }
        };

        pendingDisconnectedListener = onUADisconnected;
        ua.on('disconnected', onUADisconnected);

        // Permanent UA 'connected' listener — always attached. On every WS
        // recovery it cancels the sustained-loss timer (→ no ICE restart for
        // fast flaps), the call watchdog, and clears the reconnecting flag.
        // Independent of `pendingConnectedListener` (one-shot, used by
        // scheduleIceRestart for the ICE-restart trigger itself).
        const onUAConnected = () => {
            if (cancelled) return;
            const reconnectDurationMs = wsDisconnectedAt != null ? Date.now() - wsDisconnectedAt : null;
            const recoveredInGrace = sustainedLossTimer != null;
            wsDisconnectedAt = null;
            if (sustainedLossTimer != null) {
                clearTimeout(sustainedLossTimer);
                sustainedLossTimer = null;
                logger.info('WS recovered within grace window -- deferring ICE restart, will probe media path', {
                    source: 'networkHandler',
                    event: 'ws_recovered_no_ice_restart',
                    callId,
                    wsReconnectDurationMs: reconnectDurationMs,
                });
            } else if (reconnectDurationMs != null) {
                logger.info('WS reconnected', {
                    source: 'networkHandler',
                    event: 'ws_reconnected',
                    callId,
                    wsReconnectDurationMs: reconnectDurationMs,
                });
            }
            if (callWatchdogTimer != null) {
                clearTimeout(callWatchdogTimer);
                callWatchdogTimer = null;
            }
            reconnecting = false;

            // Media-health fallback: if we skipped the ICE restart because WS
            // came back inside the grace window, watch pc.iceConnectionState
            // for the next ICE_STATE_PROBE_WINDOW_MS and fire an ICE restart
            // if it transitions to `disconnected`/`failed`. Event-driven so
            // we catch browser consent-liveness expiry whenever it fires
            // (Chromium/Firefox can take up to ~15 s on a broken path), not
            // just at a fixed poll instant.
            if (!recoveredInGrace) return;
            if (iceStateProbeCleanup != null) {
                iceStateProbeCleanup();
                iceStateProbeCleanup = null;
            }
            if (iceStateProbeTimer != null) {
                clearTimeout(iceStateProbeTimer);
                iceStateProbeTimer = null;
            }
            const pc = session?.connection as RTCPeerConnection | undefined;
            if (!pc || pc.iceConnectionState === 'closed') return;
            // Immediate check: the candidate pair may already be marked dead
            // by the time WS came back (fast roam with advance notice).
            if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
                logger.warn(`Post-reconnect ICE probe: ${pc.iceConnectionState} already -- firing ICE restart`, {
                    source: 'networkHandler',
                    event: 'ws_recovered_ice_dead_ice_restart',
                    callId,
                    iceConnectionState: pc.iceConnectionState,
                });
                // WS is already up here (we're inside the onUAConnected
                // handler) so skip scheduleIceRestart's wait-for-connected
                // dance and restart immediately — every 100 ms of delay is
                // audible silence after the probe confirmed media is dead.
                triggerIceRestart('ws_recovered_ice_dead');
                return;
            }
            const onIceStateChange = () => {
                if (cancelled) return;
                const state = pc.iceConnectionState;
                if (state !== 'disconnected' && state !== 'failed') return;
                // Unregister first so a transient flap -> restart -> flap
                // cannot re-fire.
                if (iceStateProbeCleanup != null) {
                    iceStateProbeCleanup();
                    iceStateProbeCleanup = null;
                }
                if (iceStateProbeTimer != null) {
                    clearTimeout(iceStateProbeTimer);
                    iceStateProbeTimer = null;
                }
                logger.warn(`Post-reconnect ICE probe: state flipped to ${state} -- firing ICE restart`, {
                    source: 'networkHandler',
                    event: 'ws_recovered_ice_dead_ice_restart',
                    callId,
                    iceConnectionState: state,
                });
                // WS is already up here (we're inside the onUAConnected
                // handler) so skip scheduleIceRestart's wait-for-connected
                // dance and restart immediately — every 100 ms of delay is
                // audible silence after the probe confirmed media is dead.
                triggerIceRestart('ws_recovered_ice_dead');
            };
            pc.addEventListener('iceconnectionstatechange', onIceStateChange);
            iceStateProbeCleanup = () => {
                pc.removeEventListener('iceconnectionstatechange', onIceStateChange);
            };
            iceStateProbeTimer = setTimeout(() => {
                iceStateProbeTimer = null;
                if (iceStateProbeCleanup != null) {
                    iceStateProbeCleanup();
                    iceStateProbeCleanup = null;
                }
                if (cancelled) return;
                logger.info('Post-reconnect ICE probe window elapsed -- path intact', {
                    source: 'networkHandler',
                    event: 'ws_recovered_ice_healthy',
                    callId,
                    windowMs: ICE_STATE_PROBE_WINDOW_MS,
                    iceConnectionState: pc.iceConnectionState,
                });
            }, ICE_STATE_PROBE_WINDOW_MS);
        };
        pendingUaConnectedListener = onUAConnected;
        ua.on('connected', onUAConnected);
    }

    logger.debug(`Network change handler registered (event-driven: ${!!ua})`, {
        source: 'networkHandler',
        event: 'network_handler_registered',
        callId,
        eventDriven: !!ua,
        ...networkSummary(),
    });

    // Cleanup function
    return () => {
        cancelled = true;
        window.removeEventListener('offline', onOffline);
        window.removeEventListener('online', onOnline);
        if (connection) {
            connection.removeEventListener('change', onConnectionChange);
        }
        if (iceRestartTimer != null) {
            clearTimeout(iceRestartTimer);
            iceRestartTimer = null;
        }
        if (reconnectTimer != null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
        if (pendingConnectedListener != null && ua) {
            ua.removeListener('connected', pendingConnectedListener);
            pendingConnectedListener = null;
        }
        if (pendingDisconnectedListener != null && ua) {
            ua.removeListener('disconnected', pendingDisconnectedListener);
            pendingDisconnectedListener = null;
        }
        if (pendingUaConnectedListener != null && ua) {
            ua.removeListener('connected', pendingUaConnectedListener);
            pendingUaConnectedListener = null;
        }
        if (sustainedLossTimer != null) {
            clearTimeout(sustainedLossTimer);
            sustainedLossTimer = null;
        }
        if (iceStateProbeTimer != null) {
            clearTimeout(iceStateProbeTimer);
            iceStateProbeTimer = null;
        }
        if (iceStateProbeCleanup != null) {
            iceStateProbeCleanup();
            iceStateProbeCleanup = null;
        }
        if (callWatchdogTimer != null) {
            clearTimeout(callWatchdogTimer);
            callWatchdogTimer = null;
        }
        logger.debug('Network change handler cleaned up', {
            source: 'networkHandler',
            event: 'network_handler_cleanup',
            callId,
        });
    };
}
